import React from 'react';
import { Location } from '../types';
import { MapPin, Lock, Clock, Car, ShieldAlert } from 'lucide-react';

interface LocationCardProps {
  location: Location;
  isCurrentLocation: boolean;
  distanceToTravel: number; 
  onTravel: (loc: Location, minutes: number) => void;
  onEnter: (loc: Location) => void;
  disabled: boolean;
  gameTime: number;
}

export const LocationCard: React.FC<LocationCardProps> = ({ location, isCurrentLocation, distanceToTravel, onTravel, onEnter, disabled, gameTime }) => {
  // Use Simulated Game Time
  const now = new Date(gameTime);
  const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
  
  const [openHour, openMinute] = location.openTime.split(':').map(Number);
  const [closeHour, closeMinute] = location.closeTime.split(':').map(Number);
  
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const openMinutes = openHour * 60 + openMinute;
  const closeMinutes = closeHour * 60 + closeMinute;

  let isOpen = false;

  // 1. Crime Scene Logic (Always Open)
  if (location.isCrimeScene) {
      isOpen = true;
  } else {
      // 2. Business Hours Logic
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      // Sunday Closed rule for Public places that aren't restricted (Mock logic)
      if (dayOfWeek === 0 && location.type === 'public') {
          isOpen = false;
      } else {
          // Time check
          if (location.openTime === '00:00' && location.closeTime === '23:59') {
              isOpen = true;
          } else if (closeMinutes < openMinutes) {
              isOpen = currentMinutes >= openMinutes || currentMinutes < closeMinutes;
          } else {
              isOpen = currentMinutes >= openMinutes && currentMinutes < closeMinutes;
          }
      }
  }

  // Physics: 1 unit = 2 minutes
  const travelTimeMinutes = Math.ceil(distanceToTravel * 2);

  return (
    <div className={`p-4 border ${isCurrentLocation ? 'border-noir-accent bg-noir-800/50' : 'border-noir-700 bg-noir-900'} rounded-lg transition-all hover:border-noir-500 flex flex-col justify-between relative overflow-hidden`}>
      
      {location.isCrimeScene && (
          <div className="absolute top-0 right-0 bg-yellow-600 text-black text-[10px] font-bold px-2 py-1 flex items-center gap-1 z-10">
              <ShieldAlert className="w-3 h-3" /> CENA DO CRIME
          </div>
      )}

      <div>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-bold text-noir-text font-mono flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            {location.name}
          </h3>
          <span className="text-xs text-noir-muted bg-noir-800 px-2 py-1 rounded font-mono">
             {isCurrentLocation ? 'VOCÊ ESTÁ AQUI' : `${Math.round(distanceToTravel)} km`}
          </span>
        </div>
        <p className="text-sm text-noir-muted mb-4 line-clamp-2">{location.description}</p>
        
        <div className="flex items-center gap-2 text-xs text-noir-accent mb-4 font-mono">
            <Clock className="w-3 h-3" />
            {location.isCrimeScene ? (
                <span className="font-bold text-yellow-500">ACESSO POLICIAL LIBERADO (24H)</span>
            ) : location.openTime === '00:00' && location.closeTime === '23:59' ? (
                <span className="font-bold">ABERTO 24H</span>
            ) : (
                <>
                    {location.openTime} - {location.closeTime}
                    {!isOpen && <span className="text-red-500 ml-2 font-bold">[FECHADO AGORA]</span>}
                    {dayOfWeek === 0 && !isOpen && <span className="text-red-400 ml-1">(Dom)</span>}
                </>
            )}
        </div>
      </div>

      <div className="flex gap-2 mt-auto">
        {!isCurrentLocation ? (
             <button 
                onClick={() => onTravel(location, travelTimeMinutes)}
                disabled={disabled}
                className="w-full py-2 bg-noir-800 hover:bg-noir-700 text-noir-text text-sm font-bold border border-noir-600 rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
             >
                <Car className="w-3 h-3" />
                VIAJAR (~{travelTimeMinutes} min)
             </button>
        ) : (
            <button 
                onClick={() => onEnter(location)}
                disabled={disabled} 
                className={`w-full py-2 text-black text-sm font-bold rounded disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2 transition-colors ${isOpen ? 'bg-noir-accent hover:bg-amber-600' : 'bg-red-900/50 text-red-200 border border-red-800'}`}
            >
                {isOpen ? (location.isCrimeScene ? 'INVESTIGAR CENA' : 'INVESTIGAR LOCAL') : <><Lock className="w-3 h-3"/> FECHADO</>}
            </button>
        )}
      </div>
    </div>
  );
};